import { colorSchemeDark, colorSchemeLight, FirstDataRenderedEvent, iconSetAlpine, themeQuartz } from "ag-grid-community";
import { AgGridReact, AgGridReactProps } from "ag-grid-react";
import { ForwardedRef, forwardRef, useCallback, useMemo, useRef, type JSX } from 'react';
import "../../assets/css/elynx-ag-grid.css";
import { useAppSettings } from "../../contexts/app-context/AppContext";
import { useUserContext } from "../../contexts/user-context/UserContext";

const autoSizeDelay = 100; // milliseconds

export enum AutoSizeStrategyEnum {
    None = 0,
    FitToGrid = 1,
    FitToCellContents = 2
}

export type InternalGridProps<TData = any> = {
    showFooter?: boolean;
    spacingOverride?: number;
    fontSizeOverride?: number;
    initialMinWidth?: number;
    autoSizeStrategy?: AutoSizeStrategyEnum;
} & Omit<AgGridReactProps<TData>, "autoSizeStrategy">;

export type InternalGridRef<TData = any> = AgGridReact<TData>;

function InternalGrid<TData = any>({
    showFooter,
    spacingOverride,
    fontSizeOverride,
    initialMinWidth,
    autoSizeStrategy,
    defaultColDef,
    onFirstDataRendered,
    columnDefs,
    rowData,
    ...props
}: InternalGridProps<TData>, ref: ForwardedRef<InternalGridRef<TData>>) {

    // Debug
    // useEffect(() => {
    //     console.log("#### Created InternalGrid ####");
    //     return () => console.log("#### Destroyed InternalGrid ####");
    // }, []);

    const [appSettings] = useAppSettings();
    const themeMode = appSettings.themeSet.mode;
    const [userState] = useUserContext();
    const firstDataRendered = useRef<boolean>(false);

    const spacing = spacingOverride ?? userState?.userSettings?.gridCellPadding ?? 5;
    const fontSize = fontSizeOverride ?? userState?.userSettings?.gridFontSize ?? 13;

    const theme = useMemo(() => {
        return themeQuartz
            .withPart(themeMode === "dark"
                ? colorSchemeDark
                : colorSchemeLight
            )
            .withPart(iconSetAlpine)
            .withParams({
                fontSize,
                headerFontSize: fontSize,
                spacing
            });
    }, [
        themeMode,
        spacing,
        fontSize
    ]);

    const _onFirstDataRendered = useCallback(async (event: FirstDataRenderedEvent<TData>) => {
        if (autoSizeStrategy === AutoSizeStrategyEnum.FitToGrid ||
            autoSizeStrategy === AutoSizeStrategyEnum.FitToCellContents) {
            setTimeout(() => {
                // Debug
                console.log(
                    "#### ==================================== ####\n" +
                    "#### ========> Auto-sizing Grid <======== ####\n" +
                    "#### ==================================== ####"
                );

                if (autoSizeStrategy === AutoSizeStrategyEnum.FitToGrid) {
                    event.api.sizeColumnsToFit();
                } else if (autoSizeStrategy === AutoSizeStrategyEnum.FitToCellContents) {
                    event.api.autoSizeAllColumns({
                        skipHeader: true,
                        //scaleUpToFitGridWidth: true,
                    });
                }

                firstDataRendered.current = true;
            }, autoSizeDelay);
        }

        onFirstDataRendered?.(event);
    }, [
        autoSizeStrategy,
        onFirstDataRendered
    ]);

    const minWidth = firstDataRendered.current
        ? (fontSize * 3) + (2 * spacing)
        : initialMinWidth;

    // Debug
    // console.log(`#### InternalGrid ####\n` +
    //     `\tColumns: ${columnDefs?.length}\n` +
    //     `\tRows: ${rowData?.length}\n` +
    //     `\tModifyMinWidth: ${modifyMinWidth.current}\n` +
    //     `\tminWidth: ${minWidth}`
    // );

    return (
        <AgGridReact<TData>
            theme={theme}
            className={themeMode === "dark"
                ? "theme-dark"
                : "theme-light"
            }
            {...props}
            ref={ref}
            defaultColDef={{
                ...defaultColDef,
                initialWidth: initialMinWidth,
                minWidth: minWidth
            }}
            columnDefs={columnDefs}
            rowData={rowData}
            onFirstDataRendered={_onFirstDataRendered}
        />
    );
}

export default forwardRef(InternalGrid) as <TData>(props: InternalGridProps<TData> & { ref?: ForwardedRef<InternalGridRef<TData>>; }) => JSX.Element;